<template>
  <div>
    <select-field ref="EtablissementSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Etablissement from '@/extend/classes/Etablissement';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionEtablissementIdentiteExtend from '@/extend/sections/SectionEtablissementIdentiteExtend.vue';
  import { storeEtablissement } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listEtablissement: Etablissement[] = [];
      const tmpEtablissement: Etablissement = new Etablissement();
      return {
        selectedEtablissementId: 0,
        selectedEtablissement: new Etablissement(),
        tmpEtablissement,
        newContext: true,
        reset: true,
        listUpdated: false,
        listEtablissement
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionEtablissementIdentiteExtend;
        section.type = 'etablissement';
        section.data = this.selectedEtablissement;
        section.picto = 'SectionEtablissementIdentiteExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): Etablissement[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listEtablissement;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouvel établissement")); 
        const etablissements = this.getItems(this.listItems, this.sectionName);
        etablissements.forEach(etablissement => {
          if (etablissement && etablissement.id > 0 ) {
            items.push(new Item(etablissement.id, this.getDisplay(etablissement)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const etablissement = this.listEtablissement.find(a => a.id == itemDefined.key);
          if (etablissement && etablissement.id != this.selectedEtablissementId) {
            this.setSelectedEtablissement(etablissement);
          }
          if (item.key == 0 && this.selectedEtablissementId == 0) {
            this.setSelectedEtablissement(this.newEtablissement());
          }
          this.setSelectedEtablissementId(etablissement ? etablissement.id : this.selectedEtablissement.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedEtablissement, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedEtablissement(value: Etablissement) {
        this.selectedEtablissement = value;
      },
      setSelectedEtablissementId(id: number) {
        this.selectedEtablissementId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newEtablissement (): Etablissement {
        this.clearStore();
        const newEtablissement = storeEtablissement.createNewEtablissement();
        this.tmpEtablissement = newEtablissement;
        return newEtablissement;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            Etablissement.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as Etablissement[];
                this.listEtablissement = [];
                items.forEach((item) => {
                  this.listEtablissement.push(Etablissement.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpEtablissement != null && this.tmpEtablissement.id < 0) {
          storeEtablissement.mEtablissementDeleted(this.tmpEtablissement.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedEtablissementId = 0;
        this.selectedEtablissement = new Etablissement();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(etablissement: Etablissement[], section: String): Etablissement[] {
        const items: Etablissement[] = [];
        etablissement.forEach((item) => {
          items.push(Etablissement.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(etablissement: Etablissement): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(etablissement: Etablissement, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(etablissement: Etablissement) {
        const item = this.items.find(a => a.key == etablissement.id);
        if (!item && etablissement.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == etablissement.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedEtablissement = this.newEtablissement();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (etablissement: Etablissement) {
        this.selectedEtablissement = etablissement;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.EtablissementSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.EtablissementSummary.touch();
      }
    },
  })
</script>
